import React, { useCallback, useEffect, useReducer, useState } from "react";
import _ from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBarcode, faSlidersH, faFilter } from "@fortawesome/free-solid-svg-icons";
import * as notify from "../../shared/notify";
import eventEmitter from "../../shared/events";

import TaskNav from "../task-nav/task-nav";
import Modal from "../modal";
import NoteLoader from "../notes/components/note-loader";
import ColumnChooser from "./components/column-chooser";
import PaginatedTable from "../table";

import { getLocations, getTasks, getTaskTypes, getAssignmentOptions } from "./lib/taskData";
import { SECTION_MAP, DEFAULT_SORT, DEFAULT_FILTER, DEFAULT_COLUMNS, createColumns, advancedFiltersFields, TaskType } from "./lib/constants";
import { getStorageValue, setStorageValue } from "../../shared/storage";
import { Input } from "../form-inputs/inputs";
import { reducer, initialState } from "./reducer";

import "./task-list.scss";
import { useAlert } from "hooks/useAlert";
import { useTaskSettings } from "shared/hooks/use-task-settings";

import { getAvailableUserAssignmentGroups } from "../../admin/user-roles/lib/userData";
import { AdvancedFilters } from "../advanced-filters/advanced-filters";
import { useModal } from "../../shared/hooks/use-modal";
import { validateCoderRole } from "components/coding/lib/codingData";
import { TaskList } from "./task-list";
import { Chip } from "../chip/chip";
import { useDefaultFilters } from "../../shared/hooks/use-default-filters";
import { useAssignments } from "../../shared/hooks/use-assignments";
import { StateController } from "./components/state-controller/state-controller";
import { useUserAdvancedFilters } from "../../shared/hooks/use-user-advanced-filters";
import { useViewOnlyPermissions } from "shared/hooks/use-view-only-permissions";
import { useUSCentralTime } from "shared/hooks/use-central-time";
import { AdvancedFiltersProvider } from "../../contexts/advanced-filters/advanced-filters-provider";
import { booleanOptions } from "../../shared/constants/dropdown-options";
import { SHIPMENT_FILTER_TYPES } from "../shipments/lib/constants";
import { useNavigate, useParams } from "react-router-dom";
import { BulkUploadModal } from "./components/photography-bulk-upload/bulk-upload-modal";
import { CopyPathModal } from "./components/photography-bulk-upload/copy-path-modal";
import { TaskStatus as TaskState, TaskStatus } from "shared/constants/task-status";
import { useSettings } from "../../shared/hooks/use-settings";
import { TasksReject } from "./components/tasks-reject/tasks-reject";
import { getRowHighlight } from "./lib/get-row-highlight";

let findUpcTimer = null;
let findUpcFilter = "";

const TaskListIndex = () => {
    const navigate = useNavigate();
    const params = useParams();
    const { open: isAdvancedFiltersOpen, onOpen: handleOpenAdvancedFilters, onClose: handleCloseAdvancedFilters } = useModal();
    const { open: isPhotographyBulkUploadOpen, onOpen: handleOpenPhotographyBulkUpload, onClose: handleClosePhotographyBulkUpload } = useModal();
    const { open: isCopyPathModalOpen, onOpen: handleCopyPathModal, onClose: handleCloseCopyPathModal } = useModal();
    const { taskSettings } = useTaskSettings();
    const { toggleAlert } = useAlert();
    const { settings } = useSettings();
    const type = params.type ? params.type : "";
    const label = type ? SECTION_MAP[type].label : "";
    const taskLabel = label + " Task List";
    const sourcePage = taskSettings[type].sourcePage;
    const isImageEditingQa = type === "imageeditingqa";
    const isImageEditing = type === "imageediting";
    const isPhotography = type === "photography";
    const isFinalQa = type === "finalqa";

    const TASKLIST_FILTER = `tasklist-filter-${label.replace(" ", "").toLowerCase()}`;
    const TASKLIST_SORT = `tasklist-sort-${label.replace(" ", "").toLowerCase()}`;
    const ADVANCED_FILTER = `${type}-advanced-filters`;
    const [defaultFilter, setDefaultFilter] = useState([...DEFAULT_FILTER]);
    const [locations, setLocations] = useState([]);
    const [types, setTypes] = useState([]);
    const [searchedUpc, setSearchedUpc] = useState("");
    const [showModalColumns, setShowModalColumns] = useState(false);
    const [isReady, setIsReady] = useState(false);
    const [appendSettings, setAppendSettings] = useState(null);
    const [noteSettings, setNoteSettings] = useState(null);
    const { userFilters, externalFilters, clearFilters, clearAdvancedFilters, applyExternalFilters } = useUserAdvancedFilters(ADVANCED_FILTER);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [forceTaskListRefresh, setForceTaskListRefresh] = useState(false);
    const [bulkUploadPathInfo, setBulkUploadPathInfo] = useState();

    const [initialAssignmentParams, setInitialAssignmentParams] = useState({
        availableUsersAssignments: [],
        availableAssignments: [],
        users: [],
    });
    const { userAssignmentOptions, assignmentOptions, updateTeamAssignments, updateUserAssignments, populateUsers, populateUserAssignments } =
        useAssignments(type, initialAssignmentParams);
    const getBasicDefaultFilters = useDefaultFilters();
    const [state, dispatch] = useReducer(reducer, initialState);
    const isVOComplete = useViewOnlyPermissions("CompleteTask", type);
    const isVOReject = useViewOnlyPermissions("RejectTask", type);
    const isVOFqaBulkReject = useViewOnlyPermissions("RejectBulkFqaTasks");
    const isVOCompleteBulkUploadImagesForMultipleUpcs = useViewOnlyPermissions("CompleteBulkUploadImagesForMultipleUpcs");
    const { getNow, toCentralTime } = useUSCentralTime();
    const [disableAdvancedFilters, setDisableAdvancedFilters] = useState(true);
    const optionsAssignments = {
        isRush: booleanOptions,
        "context.matchedProductTaskContextKey": booleanOptions,
        "context.hasPremiumEditing": booleanOptions,
        isFoodService: booleanOptions,
        "context.imagesNeedClientApproval": booleanOptions,
        "locationInfo.location": locations,
        state: types,
        "context.shipmentType": SHIPMENT_FILTER_TYPES,
    };
    const isDedicatedAdvancedSearch = isImageEditing || isImageEditingQa || isPhotography || isFinalQa;
    const isApproveRejectVisible = isImageEditingQa || isPhotography || isImageEditing || isFinalQa;

    useEffect(() => {
        return () => {
            setSelectedProducts([]);
        };
    }, [sourcePage]);

    useEffect(() => {
        const { filter } = state;

        if (filter.length) {
            const assignmentGroupFilter = filter.find(({ id }) => id === "assignmentGroupKey");
            const assignedUserInfoFilter = filter.find(({ id }) => id === "assignedUserInfo");

            if (assignmentGroupFilter) {
                updateTeamAssignments(assignmentGroupFilter.value);
            }

            if (assignedUserInfoFilter) {
                updateUserAssignments(assignedUserInfoFilter.value);
            }
        }
    }, [state.filter, initialAssignmentParams.availableAssignments, initialAssignmentParams.availableUsersAssignments]);

    useEffect(() => {
        const assignMessageGroup = () => {
            switch (type) {
                case "duplicatestaging":
                    return "DuplicateStaging";
                case "codingqa":
                    return "CodingQA";

                case "imageediting":
                    return "ImageEditing";

                case "imageeditingqa":
                    return "ImageEditingQA";

                case "finalqa":
                    return "FinalQA";

                default:
                    return _.capitalize(type);
            }
        };

        if (settings.globalNotifications.messages.length) {
            toggleAlert({ group: assignMessageGroup() });
        }
    }, [settings.globalNotifications.messages.length, type]);

    useEffect(() => {
        buildPageData();
        eventEmitter.on("UPDATE_NOTE_SETTINGS", updateNoteSettings);

        return () => {
            if (findUpcTimer) {
                clearTimeout(findUpcTimer);
            }

            eventEmitter.removeListener("UPDATE_NOTE_SETTINGS", updateNoteSettings);
        };
    }, [type]);

    useEffect(() => {
        if (forceTaskListRefresh) {
            setSelectedProducts([]);
            setForceTaskListRefresh(false);
        }
    }, [forceTaskListRefresh]);

    const handleInitializeCopyPathModal = (pathDetails) => {
        handleClosePhotographyBulkUpload();
        handleCopyPathModal();
        setBulkUploadPathInfo(pathDetails);
    };

    const updateNoteSettings = (newSettings) => {
        setNoteSettings({ ...newSettings, taskType: SECTION_MAP[type].noteKey });
    };

    const getDefaultColumns = () => {
        return Object.keys(DEFAULT_COLUMNS).reduce((columns, columnKey) => {
            if (DEFAULT_COLUMNS[columnKey].notDefaultFor && Array.isArray(DEFAULT_COLUMNS[columnKey].notDefaultFor)) {
                const isNotDefaultColumn = DEFAULT_COLUMNS[columnKey].notDefaultFor.includes(sourcePage);

                if (isNotDefaultColumn) {
                    columns[columnKey] = { ...DEFAULT_COLUMNS[columnKey], value: false };

                    return columns;
                }
            }

            columns[columnKey] = { ...DEFAULT_COLUMNS[columnKey] };

            return columns;
        }, {});
    };

    const getVisibleColumnsKeys = (columns) =>
        Object.keys(state.columns)
            .reduce((arr, key) => {
                if (columns[key].value) {
                    arr.push(key);
                }

                return arr;
            }, [])
            .join(",");

    const getUserColumns = () => {
        const storedColumns = localStorage.getItem(`task-list-columns-${type}`);
        const storedColumnKeys = storedColumns || [];

        // Built off defaults to prevent old data stored from corrupting the future
        if (storedColumns) {
            return Object.keys(DEFAULT_COLUMNS).reduce((columns, columnKey) => {
                if (DEFAULT_COLUMNS[columnKey].hidden && !isImageEditingQa && !isPhotography) {
                    return columns;
                }

                const storedValue = storedColumnKeys.includes(columnKey);
                columns[columnKey] = { ...DEFAULT_COLUMNS[columnKey], value: storedValue };

                return columns;
            }, {});
        }

        return getDefaultColumns();
    };

    const buildPageData = async () => {
        setIsReady(false);

        const assignments = await getAssignmentOptions(type);
        const userAssignments = await getAvailableUserAssignmentGroups(type);
        const users = populateUsers(userAssignments.items);

        setInitialAssignmentParams({
            availableAssignments: assignments,
            availableUsersAssignments: userAssignments.items,
            users,
        });

        // TODO: move it to a hook
        Promise.all([getLocations(), getTaskTypes(type)]).then((data) => {
            const userColumns = getUserColumns();
            const [initLocations, taskTypes] = data;

            if (!taskTypes) {
                return;
            }

            const tableSettings = getStorageValue("task-list-settings") || {
                pageSize: state.pageSize,
            };
            locations.push({ text: "Digital", value: "Digital" });

            // Default & Filter
            const filter = getBasicDefaultFilters(taskTypes.defaultFilter, type, users, TASKLIST_FILTER, assignments, populateUserAssignments);

            // Sort
            const storedSort = getStorageValue(TASKLIST_SORT) || [];
            const sort = storedSort && Array.isArray(storedSort) ? storedSort : [...DEFAULT_SORT];

            setLocations(initLocations);
            setDefaultFilter([
                {
                    id: "state",
                    value: taskTypes.defaultFilter,
                },
            ]);
            setTypes(taskTypes.statusList);

            // Set search filter
            const activeFilter = taskTypes.statusList.find((f) => f.value === "active");
            findUpcFilter = activeFilter ? TaskStatus.ACTIVE : TaskStatus.TO_DO;

            setStorageValue(TASKLIST_FILTER, filter);
            setIsReady(true);
            dispatch({
                type: "SET_SETTINGS",
                columns: userColumns,
                filter: filter,
                sort: sort,
                pageSize: tableSettings.pageSize,
            });
        });
    };

    const getTaskData = (page, pageSize, sorted, filtered) => {
        return getTasks(type, page, pageSize, sorted, filtered);
    };

    const onBINClick = (event, shipmentUrl) => {
        event.preventDefault();
        event.stopPropagation();

        if (event.ctrlKey || event.metaKey) {
            window.open(`${shipmentUrl}`, "_blank");
        }
    };

    const checkCoderRole = async (key, wip, taskType) => {
        const isCoderValid = await validateCoderRole(key);

        if (!isCoderValid) {
            return notify.error(
                "Access Denied: You are either currently working on/have already completed this UPC, or you do not have permissions to open this task."
            );
        }

        gotoWip(key, wip, taskType);
    };

    const handleClick = (e, rowInfo) => {
        const key = rowInfo.original.key;
        const wip = rowInfo.original.context[`${SECTION_MAP[type].wipId}`];
        const taskType = rowInfo.original.context.taskType;

        if (type === TaskType.CODING) {
            return checkCoderRole(key, wip, taskType);
        }

        gotoWip(key, wip, taskType);
    };

    const onTaskRowClick = (s, rowInfo) => {
        const styles = {
            cursor: "pointer",
        };

        if (rowInfo) {
            const dueBackground = getRowHighlight({
                isCompleted: rowInfo.original.isCompleted,
                state: rowInfo.original.state,
                dueCentral: rowInfo.original.due ? toCentralTime(rowInfo.original.due) : null,
                now: getNow(),
            });

            if (dueBackground) {
                styles.backgroundColor = dueBackground;
            }
        }

        if (rowInfo && rowInfo.original.state === TaskState.WAITING_FOR_CA && isImageEditingQa) {
            styles.backgroundColor = "";
        }

        const style = {
            ...styles,
        };

        return {
            onDoubleClick: (e) => handleClick(e, rowInfo),
            style: style,
        };
    };

    const gotoWip = (key, wipId, taskType) => {
        const getSectionPath = () => {
            if (type) {
                return SECTION_MAP[type].path;
            } else if (SECTION_MAP[taskType.split(".")[0]]) {
                return SECTION_MAP[taskType.split(".")[0]].path;
            } else {
                return "";
            }
        };

        const section = getSectionPath();

        if (section && wipId) {
            navigate(`${section}${key}/${wipId}`);
        } else {
            const errMsg = "Task List: navPath does not exist";
            notify.error(errMsg);
            throw errMsg;
        }
    };

    const triggerFindUpc = (tasks, searchedValue) => {
        if (searchedValue) {
            const foundTasks = tasks.filter((task) => task.context.upc === searchedValue);
            setSearchedUpc("");

            if (foundTasks.length === 1) {
                const key = foundTasks[0].key;
                const wip = foundTasks[0].context[`${SECTION_MAP[type].wipId}`];
                const taskType = foundTasks[0].context.taskType;
                gotoWip(key, wip, taskType);
            } else if (foundTasks.length > 1) {
                notify.warning("More than one UPC has been found.");
                setAppendSettings({
                    page: 1,
                    filtered: [
                        { id: "context.upc", value: searchedValue },
                        { id: "state", value: findUpcFilter },
                    ],
                });
            } else {
                notify.error("UPC was not found.");
            }
        }
    };

    const findByUPC = (name, value) => {
        if (findUpcTimer) {
            clearTimeout(findUpcTimer);
        }

        const trimmedValue = value.trim();
        setSearchedUpc(trimmedValue);

        if (trimmedValue) {
            findUpcTimer = setTimeout(async () => {
                const response = await getTaskData(
                    1,
                    50,
                    [],
                    [
                        { id: "context.upc", value: trimmedValue },
                        { id: "state", value: findUpcFilter },
                    ]
                );
                triggerFindUpc(response.items, trimmedValue);
            }, 500);
        }
    };

    const closeModal = () => {
        setShowModalColumns(false);
    };

    const resetDefault = () => {
        dispatch({
            type: "SET_SETTINGS",
            columns: state.columns,
            filter: [...defaultFilter],
            sort: [...DEFAULT_SORT],
            pageSize: state.pageSize,
        });
        clearAdvancedFilters();
        setStorageValue(TASKLIST_FILTER, [...defaultFilter]);
        setStorageValue(TASKLIST_SORT, [...DEFAULT_SORT]);
        resetColumns();
        window.location.reload();
    };

    const updateUserColumns = (columns) => {
        const selectedColumns = getVisibleColumnsKeys(columns);

        dispatch({
            type: "SET_COLUMNS",
            columns: { ...columns },
        });
        setStorageValue(`task-list-columns-${type}`, selectedColumns);
        closeModal();
    };

    const resetColumns = () => {
        dispatch({
            type: "SET_COLUMNS",
            columns: getDefaultColumns(),
        });
        localStorage.removeItem(`task-list-columns-${type}`);
        closeModal();
    };

    const openColumnChooser = () => {
        setShowModalColumns(true);
    };

    const closeNotesModal = () => {
        setNoteSettings(null);
    };

    const handleRefreshOnTaskComplete = useCallback(() => setForceTaskListRefresh(true), []);

    const handleUnselectRows = useCallback(() => setSelectedProducts([]), []);

    const handleDisableAdvancedFilters = useCallback((disabled) => setDisableAdvancedFilters(disabled), []);

    return (
        <div className="container">
            <TaskNav>
                <div className="button-group">
                    <h1>{taskLabel}</h1>
                </div>
            </TaskNav>
            <div className="row">
                <div className="column -span6">
                    <div className="input-group">
                        <h1>
                            <FontAwesomeIcon icon={faBarcode} />
                        </h1>
                        <Input
                            name="scan_for_upc"
                            value={searchedUpc}
                            autoFocus={true}
                            updateOnChange={findByUPC}
                            label="Scan UPC"
                        />
                    </div>
                    {isPhotography && settings.featureFlags.photographyBulkUploadEnabled && (
                        <div className="button-group justify-content-end">
                            <button
                                className="button -no-margin -small"
                                onClick={handleOpenPhotographyBulkUpload}
                                disabled={isVOCompleteBulkUploadImagesForMultipleUpcs}
                            >
                                Bulk Upload Images
                            </button>
                        </div>
                    )}
                    <div className="button-group justify-content-between">
                        <div className="flex">
                            {isApproveRejectVisible && selectedProducts.length > 0 && (
                                <>
                                    {isFinalQa ? (
                                        <TasksReject
                                            isVOReject={isVOFqaBulkReject}
                                            sourcePage={sourcePage}
                                            onUnselectRows={handleUnselectRows}
                                            selectedUpcs={selectedProducts}
                                            onTaskCompleteRefresh={handleRefreshOnTaskComplete}
                                        />
                                    ) : (
                                        <StateController
                                            selectedProducts={selectedProducts}
                                            onTaskCompleteRefresh={handleRefreshOnTaskComplete}
                                            onUnselectRows={handleUnselectRows}
                                            isVOComplete={isVOComplete}
                                            isVOReject={isVOReject}
                                            isPhotography={isPhotography}
                                            sourcePage={sourcePage}
                                        />
                                    )}
                                </>
                            )}
                        </div>
                        <div className="flex align-items-center">
                            {isDedicatedAdvancedSearch && (
                                <div className="m-l-12 m-r-12">
                                    <Chip
                                        disabled={disableAdvancedFilters}
                                        label="Advanced Filters"
                                        icon={faFilter}
                                        onClick={handleOpenAdvancedFilters}
                                        onClear={clearAdvancedFilters}
                                        value={externalFilters && externalFilters.length}
                                    />
                                </div>
                            )}
                            <button
                                className="button -small -right"
                                onClick={resetDefault}
                            >
                                Reset to Default Sort/Filter
                            </button>
                            <button
                                className="button -small -right"
                                onClick={openColumnChooser}
                            >
                                <FontAwesomeIcon icon={faSlidersH} />
                            </button>
                            <Modal
                                show={showModalColumns}
                                onClose={closeModal}
                            >
                                <ColumnChooser
                                    type={type}
                                    updateUserColumns={updateUserColumns}
                                    resetColumns={resetColumns}
                                    userColumns={state.columns}
                                />
                            </Modal>
                            {isDedicatedAdvancedSearch && isAdvancedFiltersOpen && (
                                <AdvancedFiltersProvider
                                    optionsAssignments={optionsAssignments}
                                    filtersModel={advancedFiltersFields}
                                    onClose={handleCloseAdvancedFilters}
                                    onFiltersSubmit={(filters) => applyExternalFilters(filters, false, handleCloseAdvancedFilters)}
                                    storedFilters={userFilters}
                                    clearFilters={clearFilters}
                                    locations={locations}
                                    statusTypes={types}
                                    initialAssignmentParams={initialAssignmentParams}
                                    task={type}
                                    defaultFilter={defaultFilter}
                                >
                                    <AdvancedFilters open={isAdvancedFiltersOpen} />
                                </AdvancedFiltersProvider>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {isReady && isDedicatedAdvancedSearch ? (
                <TaskList
                    onDisableAdvancedFilters={handleDisableAdvancedFilters}
                    forceRefresh={forceTaskListRefresh}
                    selectedProducts={selectedProducts}
                    onSelectProduct={setSelectedProducts}
                    visibleColumns={state.columns}
                    onBINClick={onBINClick}
                    onClick={handleClick}
                    locations={locations}
                    types={types}
                    assignmentOptions={assignmentOptions}
                    userAssignmentOptions={userAssignmentOptions}
                    onUpdateTeamAssignments={updateTeamAssignments}
                    onUpdateUserAssignments={updateUserAssignments}
                    sourcePage={sourcePage}
                    onTaskRowClick={onTaskRowClick}
                    storageSortValue={TASKLIST_SORT}
                    storageFilterValue={TASKLIST_FILTER}
                    task={type}
                    pollTimeout={15000}
                    externalFilters={externalFilters}
                    basicDefaultFilters={defaultFilter}
                />
            ) : (
                isReady && (
                    <PaginatedTable
                        getData={getTaskData}
                        appendSettings={appendSettings}
                        columns={createColumns(
                            null,
                            null,
                            null,
                            state.columns,
                            onBINClick,
                            handleClick,
                            locations,
                            types,
                            assignmentOptions,
                            userAssignmentOptions,
                            updateTeamAssignments,
                            updateUserAssignments,
                            sourcePage,
                            true,
                            settings
                        )}
                        onTaskRowClick={onTaskRowClick}
                        pollTimeout={15000}
                        showPagination={true}
                        storageSortValue={TASKLIST_SORT}
                        storageFilterValue={TASKLIST_FILTER}
                    />
                )
            )}
            <Modal
                show={!!noteSettings}
                onClose={closeNotesModal}
            >
                <NoteLoader settings={noteSettings} />
            </Modal>
            {isPhotographyBulkUploadOpen && (
                <BulkUploadModal
                    open={isPhotographyBulkUploadOpen}
                    onClose={handleClosePhotographyBulkUpload}
                    onUploadSuccess={handleInitializeCopyPathModal}
                />
            )}
            {bulkUploadPathInfo && (
                <CopyPathModal
                    open={isCopyPathModalOpen}
                    onClose={handleCloseCopyPathModal}
                    pathInfo={bulkUploadPathInfo}
                />
            )}
        </div>
    );
};

export default TaskListIndex;
